import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setNotification } from "../../redux/slices/notificationSlice";
import { setPaymentToken } from "../../redux/slices/paymentSlice";
import { getPaymentLinkHeaders } from "../../utils/paymentLinkHeaders";
import { authorizedFetch, SessionExpiredError } from "../../utils/authorizedFetch";
import { PaymentLinkErrorCode, createErrorNavigationState, getErrorTitle, getErrorMessage } from "../../utils/paymentLinkErrorHandler";
import PaymentLayout from "../layouts/PaymentLinkLayouts";
import formatCurrency from "../../utils/numberFormatter";
import { XCircle, Clock, Loader2 } from "lucide-react";
import METHOD_LOGO from "../components/MethodLogos";
import { getExpiredRedirectUrl, getSuccessRedirectUrl, redirectToMerchantUrl } from "../utils/paymentLinkRedirects";
import useTranslation from "../hooks/useTranslation";

const BASE_URL = process.env.REACT_APP_API_URL;
const MAX_POLL_DURATION = 75000;
const POLL_INTERVAL = 5000;

function maskPhone(phone) {
  if (!phone || phone.length < 6) return phone || "";
  const prefix = phone.substring(0, 4);
  const suffix = phone.substring(phone.length - 4);
  return `${prefix}****${suffix}`;
}

const OVO_FAILED_REASON_KEYS = {
  "14": "14",
  "17": "17",
  "26": "26",
  "TIMEOUT": "Timeout",
  "40": "40",
  "54": "54",
};

function getOvoFailedInfo(reasonCode, reasonText, t) {
  const matchedCode =
    (reasonCode && OVO_FAILED_REASON_KEYS[reasonCode]) ||
    (reasonText &&
      typeof reasonText === "string" &&
      OVO_FAILED_REASON_KEYS[
        Object.keys(OVO_FAILED_REASON_KEYS).find((key) => reasonText.toLowerCase().includes(key.toLowerCase()))
      ]);

  if (matchedCode) {
    return {
      title: t(`ovoWaitingPage.failedReasonTitle${matchedCode}`),
      message: t(`ovoWaitingPage.failedReasonMessage${matchedCode}`),
    };
  }

  return {
    title: t("ovoWaitingPage.failedReasonTitleDefault"),
    message: reasonText || t("ovoWaitingPage.failedReasonMessageDefault"),
  };
}

export default function OvoWaitingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { reffNo } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const paymentLinkToken = useSelector((state) => state.payment.token);

  const [pageState, setPageState] = useState("loading"); // loading | waiting | failed | timeout
  const [failedReasonCode, setFailedReasonCode] = useState("");
  const [failedReasonText, setFailedReasonText] = useState("");
  const [countdown, setCountdown] = useState(60);
  const [historyId, setHistoryId] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [customerPhone, setCustomerPhone] = useState("");
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  const pollStartRef = useRef(null);
  const pollTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const stoppedRef = useRef(false);

  const navigateToSuccess = useCallback((transaction) => {
    const successRedirectUrl = transaction?.success_redirect_url || getSuccessRedirectUrl(paymentData?.result?.data?.paymentLink);

    if (redirectToMerchantUrl(successRedirectUrl)) {
      return;
    }

    navigate("/payment-success", {
      replace: true,
      state: {
        reffNo: transaction?.reff_no || reffNo,
        amount: transaction?.amount || paymentData?.amount,
        customerName: transaction?.customer_name || null,
        email: transaction?.customer_email || null,
        paymentMethod: transaction?.payment_method || "OVO",
        paymentDate: transaction?.payment_date || "-",
        redirectUrl: successRedirectUrl,
      },
    });
  }, [navigate, reffNo, paymentData]);

  const navigateToExpired = useCallback((transaction) => {
    const expiredRedirectUrl = transaction?.expired_redirect_url || getExpiredRedirectUrl(paymentData?.result?.data?.paymentLink);

    if (redirectToMerchantUrl(expiredRedirectUrl)) {
      return;
    }

    navigate("/payment-expired", {
      replace: true,
      state: {
        selectedMethod: transaction?.payment_method || "OVO",
        reffNo: transaction?.reff_no || reffNo,
        amount: transaction?.amount || paymentData?.amount,
        redirectUrl: expiredRedirectUrl,
      },
    });
  }, [navigate, reffNo, paymentData]);

  const checkStatus = useCallback(async (hId) => {
    const effectiveHistoryId = hId || historyId;
    if (!effectiveHistoryId) return null;

    const response = await authorizedFetch(`${BASE_URL}/api/payment-link/check-status`, {
      dispatch,
      token: paymentLinkToken,
      refresh: { kind: "history", reffNo },
      method: "POST",
      body: JSON.stringify({
        history_id: effectiveHistoryId,
        reff_no: reffNo,
      }),
    });

    const data = await response.json();
    return data.transaction;
  }, [historyId, reffNo, paymentLinkToken, dispatch]);

  const handleSingleStatusCheck = useCallback(async () => {
    setIsCheckingStatus(true);
    try {
      const transaction = await checkStatus();
      if (!transaction) return;

      if (transaction.status === "paid") {
        navigateToSuccess(transaction);
      } else if (transaction.status === "expired") {
        navigateToExpired(transaction);
      } else if (transaction.status === "failed") {
        setFailedReasonCode(transaction.failed_reason_code || "");
        setFailedReasonText(transaction.failed_reason || "");
        setPageState("failed");
      } else {
        dispatch(setNotification({
          type: "info",
          title: t("ovoWaitingPage.statusNotifTitle"),
          message: t("ovoWaitingPage.paymentInProgress"),
        }));
      }
    } catch (err) {
      console.error("Error checking status:", err);
      dispatch(setNotification(
        err instanceof SessionExpiredError
          ? {
              type: "error",
              title: getErrorTitle(PaymentLinkErrorCode.SESSION_LOST),
              message: getErrorMessage(PaymentLinkErrorCode.SESSION_LOST),
            }
          : {
              type: "error",
              title: t("ovoWaitingPage.errorTitle"),
              message: t("ovoWaitingPage.errorCheckStatus"),
            },
      ));
    } finally {
      setIsCheckingStatus(false);
    }
  }, [checkStatus, navigateToSuccess, navigateToExpired, dispatch, t]);

  // Start polling
  const startPolling = useCallback((hId) => {
    stoppedRef.current = false;
    pollStartRef.current = Date.now();

    pollTimerRef.current = setInterval(async () => {
      if (stoppedRef.current) return;

      const elapsed = Date.now() - pollStartRef.current;
      if (elapsed >= MAX_POLL_DURATION) {
        clearInterval(pollTimerRef.current);
        setPageState("timeout");
        return;
      }

      try {
        const transaction = await checkStatus(hId);
        if (!transaction || stoppedRef.current) return;

        if (transaction.status === "paid") {
          stoppedRef.current = true;
          clearInterval(pollTimerRef.current);
          navigateToSuccess(transaction);
        } else if (transaction.status === "failed") {
          stoppedRef.current = true;
          clearInterval(pollTimerRef.current);
          setFailedReasonCode(transaction.failed_reason_code || "");
          setFailedReasonText(transaction.failed_reason || "");
          setPageState("failed");
        } else if (transaction.status === "expired") {
          stoppedRef.current = true;
          clearInterval(pollTimerRef.current);
          navigateToExpired(transaction);
        }
      } catch (err) {
        console.error("Polling error:", err);
        if (err instanceof SessionExpiredError) {
          stoppedRef.current = true;
          clearInterval(pollTimerRef.current);
          dispatch(setNotification({
            type: "error",
            title: getErrorTitle(PaymentLinkErrorCode.SESSION_LOST),
            message: getErrorMessage(PaymentLinkErrorCode.SESSION_LOST),
          }));
        }
      }
    }, POLL_INTERVAL);
  }, [checkStatus, navigateToSuccess, navigateToExpired, dispatch]);

  // Initialize: from navigation state or fetch from API
  useEffect(() => {
    const initFromState = () => {
      const state = location.state;
      if (state?.result) {
        const hId = state.result.data.paymentLinkHistory.id;
        setHistoryId(hId);
        setPaymentData({
          amount: state.amount,
          orderId: state.orderId,
          result: state.result,
        });
        setCustomerPhone(state.customerPhone || "");
        setPageState("waiting");
        startPolling(hId);
      } else if (reffNo) {
        fetchAndInit(reffNo);
      } else {
        navigate("/wrong-page");
      }
    };

    const fetchAndInit = async (reffNoParam) => {
      try {
        const response = await fetch(`${BASE_URL}/api/payment-link/get-by-reff`, {
          method: "POST",
          headers: getPaymentLinkHeaders(),
          body: JSON.stringify({ reff_no: reffNoParam }),
        });

        if (!response.ok) {
          navigate("/wrong-page");
          return;
        }

        const data = await response.json();
        const history = data.data.paymentLinkHistory;
        const hId = history.id;

        // get-by-reff issues a fresh payment-link-scoped token (needed here since this
        // path runs on a direct page load, where any previous in-memory token is gone).
        dispatch(setPaymentToken(data.token));

        setHistoryId(hId);
        setPaymentData({
          amount: history.amount,
          orderId: history.reff_no,
          result: data,
        });

        let additional = {};
        try {
          additional = JSON.parse(history.payment_method_additional || "{}");
        } catch (e) { /* ignore */ }

        setCustomerPhone(additional.customer_phone_masked || "");

        if (history.status === "paid") {
          navigateToSuccess({ reff_no: history.reff_no, amount: history.amount, payment_method: history.payment_method_name });
          return;
        }
        if (history.status === "expired") {
          navigateToExpired({ reff_no: history.reff_no, amount: history.amount, payment_method: history.payment_method_name });
          return;
        }
        if (history.status === "failed") {
          setFailedReasonCode(additional.ovo_failed_reason_code || "");
          setFailedReasonText(additional.ovo_failed_reason || "");
          setPageState("failed");
          return;
        }

        setPageState("waiting");
        startPolling(hId);
      } catch (err) {
        console.error("Error fetching payment data:", err);
        navigate("/wrong-page");
      }
    };

    initFromState();

    return () => {
      stoppedRef.current = true;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    };
  }, []); // Run once on mount

  // Countdown timer (visual only, 60s) — persists across refresh via sessionStorage
  useEffect(() => {
    if (pageState !== "waiting") return;

    const storageKey = `ovo_countdown_start_${reffNo}`;

    let startTime = parseInt(sessionStorage.getItem(storageKey), 10);
    if (!startTime || isNaN(startTime)) {
      startTime = Date.now();
      sessionStorage.setItem(storageKey, startTime.toString());
    }

    const getRemaining = () => Math.max(0, 60 - Math.floor((Date.now() - startTime) / 1000));

    setCountdown(getRemaining());

    if (getRemaining() <= 0) return;

    countdownTimerRef.current = setInterval(() => {
      const rem = getRemaining();
      setCountdown(rem);
      if (rem <= 0) {
        clearInterval(countdownTimerRef.current);
      }
    }, 1000);

    return () => {
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    };
  }, [pageState, reffNo]);

  const merchantName = paymentData?.result?.data?.paymentLink?.account?.merchant?.name || "";
  const amount = paymentData?.amount || 0;
  const [integerPart] = formatCurrency(amount).split(",");
  const maskedPhone = maskPhone(customerPhone);

  // Derive the parent PaymentLink reff_no for navigation back to SecondForm
  const paymentLinkReffNo = paymentData?.result?.data?.paymentLink?.reff_no || "";

  if (pageState === "loading") {
    return (
      <PaymentLayout title={merchantName || "OVO"}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-purple-600" size={40} />
        </div>
      </PaymentLayout>
    );
  }

  if (pageState === "failed") {
    const errorInfo = getOvoFailedInfo(failedReasonCode, failedReasonText, t);

    return (
      <PaymentLayout title={merchantName || "OVO"}>
        <div className="text-center py-6">
          <XCircle size={64} className="text-red-500 mx-auto" />
          <h2 className="text-2xl font-bold mt-4 text-gray-800">{errorInfo.title}</h2>
          <p className="text-gray-600 mt-2">{errorInfo.message}</p>

          {failedReasonCode === "26" && (
            <div className="bg-amber-50 rounded-xl p-4 mt-4 text-left">
              <p className="text-xs text-amber-700 leading-relaxed">{t("ovoWaitingPage.hint26")}</p>
            </div>
          )}

          {failedReasonCode === "14" && (
            <div className="bg-amber-50 rounded-xl p-4 mt-4 text-left">
              <p className="text-xs text-amber-700 leading-relaxed">{t("ovoWaitingPage.hint14")}</p>
            </div>
          )}
        </div>

        {paymentLinkReffNo && (
          <button
            onClick={() => navigate(`/method/${paymentLinkReffNo}`, { replace: true })}
            className="w-full text-white font-bold py-4 px-6 rounded-xl transition-all"
            style={{ backgroundColor: "#4C3494" }}
          >
            {t("ovoWaitingPage.tryAgain")}
          </button>
        )}
      </PaymentLayout>
    );
  }

  if (pageState === "timeout") {
    return (
      <PaymentLayout title={merchantName || "OVO"}>
        <div className="text-center py-6">
          <Clock size={64} className="text-amber-500 mx-auto" />
          <h2 className="text-xl font-bold mt-4 text-gray-800">
            {t("ovoWaitingPage.timeoutTitle")}
          </h2>
          <p className="text-gray-600 mt-3 leading-relaxed">
            {t("ovoWaitingPage.timeoutBody")}
          </p>
        </div>

        <button
          onClick={handleSingleStatusCheck}
          disabled={isCheckingStatus}
          className="w-full text-white font-bold py-4 px-6 rounded-xl transition-all disabled:opacity-50"
          style={{ backgroundColor: "#4C3494" }}
        >
          {isCheckingStatus ? t("ovoWaitingPage.checking") : t("ovoWaitingPage.checkStatus")}
        </button>

        {paymentLinkReffNo && (
          <button
            onClick={() => navigate(`/method/${paymentLinkReffNo}`, { replace: true })}
            className="w-full font-bold py-4 px-6 rounded-xl transition-all mt-3 border-2 border-gray-300 text-gray-700"
          >
            Coba Lagi
          </button>
        )}
      </PaymentLayout>
    );
  }

  // pageState === "waiting"
  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;
  const countdownDisplay = `${minutes}:${String(seconds).padStart(2, "0")}`;

  return (
    <PaymentLayout title={merchantName || "OVO"}>
      <div>
        <h2 className="text-4xl font-bold">{integerPart}</h2>
        <p className="text-gray-600 mt-2">
          {t("paymentInstructionLayout.reffNo")} {reffNo}
        </p>
        <div className="text-gray-600">{t("ovoWaitingPage.method")} OVO</div>
      </div>

      <div className="text-center py-6">
        <img
          src={METHOD_LOGO.EWALLET_OVO}
          alt="OVO"
          className="h-14 mx-auto mb-4 object-contain"
        />

        <h2 className="text-xl font-bold text-gray-800 mb-2">
          {t("ovoWaitingPage.waitingTitle")}
        </h2>

        <p className="text-gray-600 text-sm leading-relaxed mb-1">
          {t("ovoWaitingPage.sentNotification")}
          {maskedPhone && (
            <>
              {" "}
              {t("ovoWaitingPage.atNumber")} <span className="font-semibold">{maskedPhone}</span>
            </>
          )}
        </p>

        <p className="text-gray-600 text-sm mb-5">{t("ovoWaitingPage.completeWithin")}</p>

        <div className="inline-flex items-center justify-center bg-purple-50 border-2 border-purple-200 rounded-xl px-6 py-3 mb-5">
          <span className="text-3xl font-bold text-purple-700 font-mono">
            {countdownDisplay}
          </span>
        </div>

        <div className="flex items-center justify-center gap-2 text-purple-600 mb-5">
          <Loader2 className="animate-spin" size={18} />
          <span className="text-sm font-medium">{t("ovoWaitingPage.processing")}</span>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 text-left">
          <p className="text-xs text-gray-500 font-medium mb-1">
            {t("ovoWaitingPage.notReceivedTitle")}
          </p>
          <p className="text-xs text-gray-500 leading-relaxed">{t("ovoWaitingPage.notReceivedBody")}</p>
        </div>
      </div>
    </PaymentLayout>
  );
}
